/**
 * Event Modal Component
 *
 * Create and edit modal for Event records with 5 tabs
 * Special features: datetime pickers, duration calculation, cascading dropdowns
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, StickyNote, Paperclip, History, Link2 } from 'lucide-react';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { SearchableSelect } from './SearchableSelect';
import { persistedClientRecords } from '../../services/mockData/clients';
import { persistedContactRecords } from '../../services/mockData/contacts';
import { persistedOpportunityRecords } from '../../services/mockData/opportunities';
import { persistedCaseRecords } from '../../services/mockData/cases';
import { persistedEventRecords } from '../../services/mockData/events';
import { getRecordsForObject, createRecord, updateRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { ListViewRecord } from '../../types/listView';
import { useToast } from '../../contexts/ToastContext';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { ActivityTab } from '../tabs/ActivityTab';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: ListViewRecord;
}

export const EventModal: React.FC<EventModalProps> = ({ isOpen, onClose, event }) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!event;

  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Event__c', event);

  console.log('📝 Event Modal - Page Layout:', {
    eventId: event?.id,
    layoutName: pageLayout?.name,
    sectionCount: pageLayout?.sections?.length
  });

  // Define tabs based on mode
  const tabs = isEditMode
    ? [
        // EDIT MODE: All tabs
        { id: 'overview', label: 'Overview', icon: Users },
        { id: 'related', label: 'Related', icon: Link2 },
        { id: 'attendees', label: 'Attendees', icon: Users },
        { id: 'notes', label: 'Notes', icon: StickyNote },
        { id: 'files', label: 'Files', icon: Paperclip },
        { id: 'history', label: 'History', icon: History }
      ]
    : [
        // CREATE MODE: Overview only
        { id: 'overview', label: 'Overview', icon: Users }
      ];

  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({});

  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoEventModalData } = useQuery({
    queryKey: ['eventModalRecords'],
    queryFn: async () => {
      const [clients, contacts, opportunities, cases] = await Promise.all([
        getRecordsForObject('Client__c'),
        getRecordsForObject('Contact__c'),
        getRecordsForObject('Opportunity__c'),
        getRecordsForObject('Case__c')
      ]);
      return { clients, contacts, opportunities, cases };
    },
    enabled: shouldGobodo && isOpen,
    staleTime: 60 * 1000
  });
  const allClients = (shouldGobodo && gobodoEventModalData?.clients) ? gobodoEventModalData.clients : persistedClientRecords.get();
  const allContacts = (shouldGobodo && gobodoEventModalData?.contacts) ? gobodoEventModalData.contacts : persistedContactRecords.get();
  const allOpportunities = (shouldGobodo && gobodoEventModalData?.opportunities) ? gobodoEventModalData.opportunities : persistedOpportunityRecords.get();
  const allCases = (shouldGobodo && gobodoEventModalData?.cases) ? gobodoEventModalData.cases : persistedCaseRecords.get();

  // Calculate duration from start/end times
  const duration = useMemo(() => {
    if (!formData.startDateTime || !formData.endDateTime) return '';

    const start = new Date(formData.startDateTime);
    const end = new Date(formData.endDateTime);

    if (end <= start) return 'Invalid duration';

    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins} minutes`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
    }
  }, [formData.startDateTime, formData.endDateTime]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Get cascading dropdown options based on Related To selection
  const relatedRecordOptions = useMemo(() => {
    switch (formData.relatedTo) {
      case 'Client':
        return allClients.map(c => ({
          value: c.id,
          label: c.name as string
        }));
      case 'Contact':
        return allContacts.map(c => ({
          value: c.id,
          label: `${c.firstName} ${c.lastName}`
        }));
      case 'Opportunity':
        return allOpportunities.map(o => ({
          value: o.id,
          label: o.opportunityName as string
        }));
      case 'Case':
        return allCases.map(c => ({
          value: c.id,
          label: `${c.caseNumber} - ${c.subject}`
        }));
      default:
        return [];
    }
  }, [formData.relatedTo, allClients, allContacts, allOpportunities, allCases]);

  // Reset related record when Related To changes
  useEffect(() => {
    if (formData.relatedRecordId) {
      const belongsToRelatedTo = relatedRecordOptions.some(
        opt => opt.value === formData.relatedRecordId
      );
      if (!belongsToRelatedTo) {
        setFormData(prev => ({ ...prev, relatedRecordId: '' }));
      }
    }
  }, [formData.relatedTo, formData.relatedRecordId, relatedRecordOptions]);

  // Populate form from record properties directly
  useEffect(() => {
    if (event && isOpen) {
      const data: Record<string, any> = {};
      for (const [key, value] of Object.entries(event)) {
        if (value !== undefined && value !== null && typeof value !== 'object') {
          data[key] = value;
        }
      }
      setFormData(data);
      setNotes('');
      setErrors({});
    } else if (!event && isOpen) {
      setFormData({});
      setNotes('');
      setErrors({});
    }
  }, [event, isOpen]);

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      handleFieldChange(name, checked);
    } else {
      handleFieldChange(name, value);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const subject = String(formData.subject || formData.Subject__c || '').trim();
    if (!subject) {
      newErrors.subject = 'Subject is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const gobodoData: Record<string, any> = {};
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined && value !== null && value !== '' && key !== 'id' && key !== 'label') {
          gobodoData[key] = value;
        }
      }

      if (isEditMode && event) {
        const updated = await updateRecord('Event__c', String(event.id), gobodoData);
        if (!updated) throw new Error('Failed to update event');
        return { ...event, ...updated };
      } else {
        const created = await createRecord('Event__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventListViews'] });
      queryClient.invalidateQueries({ queryKey: ['eventListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['defaultEventListView'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Event__c'] });
      success(
        isEditMode ? 'Event updated' : 'Event created',
        isEditMode ? 'Event has been updated successfully' : 'New event has been created'
      );
      onClose();
    },
    onError: (error: any) => {
      console.error('[EventModal] Save error:', error);
      showError('Save failed', error?.message || 'Failed to save event. Please try again.');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    saveMutation.mutate(formData);
  };

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Event' : 'New Event'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Event'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                pageLayout.sections.map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="Event__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={saveMutation.isPending}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Event.</p>
                  <p className="text-sm mt-2">Go to Object Manager to configure page layouts.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && event?.id && (
            <RelatedRecordsTab
              recordId={event.id}
              objectType="Event__c"
              recordName={event.subject as string}
            />
          )}

          {activeTab === 'attendees' && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No attendees yet
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Event attendees will appear here.
              </p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add Note
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter event notes..."
                />
              </div>
              <div className="text-center py-8">
                <StickyNote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No notes yet. Add your first note above.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="text-center py-12">
              <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No files attached
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Event attachments will appear here.
              </p>
            </div>
          )}

          {activeTab === 'history' && event?.id && (
            <ActivityTab
              recordId={event.id}
              objectType="Event__c"
              recordName={event.subject as string}
            />
          )}
        </form>
      )}
    </TabbedModal>
  );
};
